import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";
import formOptions from "../../config/formOptions.json";
import { sortBySeq, arrayMove } from "../../utils/list";
import { parseUKDate } from "../../utils/dateUtiles";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import MarkdownEditor from "../../components/MarkdownEditor";
import LabelSelector from "../../components/LabelSelector";
import DateInput from "../../components/DateInput";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import { graphqlEndpoint } from "../../config";
import { RANK_TASK, DELETE_TASK } from "../../graph/tasks/mutations";
import { READ_PROJECT, READ_PROJECTS_FILTER } from "../../graph/projects/queries";
import { UPDATE_PROJECT } from "../../graph/projects/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const allowedLabels =
  (formOptions?.labelOptions || []).map((l) => l.value) || [];

const validationSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  description: yup.string().nullable(),
  labels: yup.array().of(yup.string().oneOf(allowedLabels, "Invalid label")),
  dueDate: yup.string().nullable(),
  status: yup.string().required("Status is required"),
  sourceProjectId: yup.string().nullable(),
  isSOPTemplate: yup.boolean(),
});

const EditSOPForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, tenantName } = useTenant();
  const { setSnackbar } = useSnackbar();

  const [initialValues, setInitialValues] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [sourceProjects, setSourceProjects] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const redirectPath = location.state?.redirectPath || "/manageSOPs";

  const fetchSOP = async () => {
    if (!tenantId) return;

    try {
      const [{ readSingleProjectById }, { readProjectsFilter }] =
        await Promise.all([
          client.request(READ_PROJECT, { id }),
          client.request(READ_PROJECTS_FILTER, {
            filter: {
              tenantId,
              isSOPTemplate: true,
            },
          }),
        ]);

      const project = readSingleProjectById;

      if (!project) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "SOP not found",
        });
        navigate(redirectPath);
        return;
      }

      if (project.tenantId !== tenantId) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "This SOP does not belong to the selected tenant",
        });
        navigate(redirectPath);
        return;
      }

      if (!project.isSOPTemplate) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "This record is not an SOP template",
        });
        navigate(redirectPath);
        return;
      }

      setSourceProjects((readProjectsFilter || []).filter((p) => p.id !== id));

      setInitialValues({
        id: project.id,
        title: project.title || "",
        description: project.description || "",
        labels: project.labels || [],
        dueDate: project.dueDate || "",
        status: project.status || "ACTIVE",
        isSOPTemplate: project.isSOPTemplate ?? true,
        sourceProjectId: project.sourceProjectId || "",
        archivedAt: project.archivedAt || null,
      });

      setTasks(sortBySeq((project.tasks || []).filter((task) => task.isLatest)));
    } catch (error) {
      console.error("Error fetching SOP:", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load SOP",
      });
    }
  };

  useEffect(() => {
    fetchSOP();
  }, [id, tenantId]);

  const handleConfirmDelete = async () => {
    if (!selectedTask?.id) return;

    try {
      await client.request(DELETE_TASK, { id: selectedTask.id });
      setTasks((prev) => prev.filter((task) => task.id !== selectedTask.id));
      setDeleteModalOpen(false);
      setSelectedTask(null);

      setSnackbar({
        open: true,
        severity: "success",
        message: "Task deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete task",
      });
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      await client.request(UPDATE_PROJECT, {
        input: {
          id: values.id,
          title: values.title.trim(),
          description: values.description?.trim() || null,
          labels: values.labels || [],
          dueDate: values.dueDate ? parseUKDate(values.dueDate) : null,
          status: values.status,
          isSOPTemplate: true,
          sourceProjectId: values.sourceProjectId || null,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "SOP updated successfully",
      });

      navigate(redirectPath, { state: { refresh: true } });
    } catch (error) {
      console.error("Error updating SOP:", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update SOP",
      });
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="EDIT SOP"
          subtitle="Select a tenant before editing an SOP"
        />
        <Typography mt={3}>
          Use the tenant selector in the top bar, then return here.
        </Typography>
      </Box>
    );
  }

  if (!initialValues) return <div>Loading...</div>;

  return (
    <Box>
      <Header
        title="EDIT SOP"
        subtitle={`Edit ${initialValues.title}${tenantName ? ` for ${tenantName}` : ""}`}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="20px" gridTemplateColumns="repeat(4, minmax(0, 1fr))">
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.title && !!errors.title}
                helperText={touched.title && errors.title}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.description}
                onChange={(nextValue) => setFieldValue("description", nextValue)}
                label="Description"
                minRows={14}
                error={!!touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {(formOptions.projectStatusOptions || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DateInput
                label="Due Date"
                name="dueDate"
                value={values.dueDate}
                onChange={setFieldValue}
                onBlur={handleBlur}
                setFieldValue={setFieldValue}
                error={errors.dueDate}
                touched={touched.dueDate}
              />

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Source SOP</InputLabel>
                <Select
                  name="sourceProjectId"
                  value={values.sourceProjectId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {sourceProjects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Type"
                value="SOP Template"
                InputProps={{ readOnly: true }}
                sx={{ gridColumn: "span 2" }}
              />

              <LabelSelector
                selectedLabels={values.labels}
                setFieldValue={setFieldValue}
                error={errors.labels}
                touched={touched.labels}
              />

              {values.archivedAt && (
                <Box sx={{ gridColumn: "span 4" }}>
                  <Typography variant="body2" color="warning.main">
                    This SOP is archived and should generally not be edited.
                  </Typography>
                </Box>
              )}
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <ActionButton type="submit" tone="success">
                Save Changes
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>

      <Box mt="40px">
        <Header title="RELATED TASKS" subtitle="Tasks linked to this SOP" />

        <ActionButton
          tone="success"
          size="small"
          onClick={() =>
            navigate("/createTask", {
              state: {
                projectId: id,
                redirectPath: `/sops/edit/${id}`,
              },
            })
          }
        >
          + Task
        </ActionButton>

        <DragDropContext
          onDragEnd={async ({ source, destination }) => {
            if (!destination) return;
            if (destination.index === source.index) return;

            const previousTasks = [...tasks];
            const next = arrayMove(tasks, source.index, destination.index);
            setTasks(next);

            const moved = next[destination.index];
            const prevSeq = next[destination.index - 1]?.sequence ?? null;
            const nextSeq = next[destination.index + 1]?.sequence ?? null;

            try {
              const { rankTask } = await client.request(RANK_TASK, {
                input: { id: moved.id, prev: prevSeq, next: nextSeq },
              });

              setTasks((cur) =>
                cur.map((t) =>
                  t.id === moved.id ? { ...t, sequence: rankTask.sequence } : t
                )
              );
            } catch (e) {
              console.error("Rank update failed", e);
              setSnackbar({
                open: true,
                severity: "error",
                message: "Failed to reorder step",
              });
              setTasks(previousTasks);
            }
          }}
        >
          <Droppable droppableId="sopTasks" type="ROW">
            {(dropProvided) => (
              <Table
                ref={dropProvided.innerRef}
                {...dropProvided.droppableProps}
                size="small"
              >
                <TableHead>
                  <TableRow>
                    <TableCell width={48}></TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.map((row, index) => (
                    <Draggable draggableId={row.id} index={index} key={row.id}>
                      {(dragProvided, snapshot) => (
                        <TableRow
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          hover
                          sx={{
                            opacity: snapshot.isDragging ? 0.7 : 1,
                            transition: "background 120ms ease",
                          }}
                        >
                          <TableCell width={48}>
                            <IconButton
                              {...dragProvided.dragHandleProps}
                              size="small"
                              aria-label="Drag row"
                            >
                              <DragIndicatorIcon />
                            </IconButton>
                          </TableCell>
                          <TableCell>{row.title}</TableCell>
                          <TableCell>{row.dueDate || "—"}</TableCell>
                          <TableCell>{row.versionMajor}.{row.versionMinor}</TableCell>
                          <TableCell align="right">
                            <Box display="flex" gap={1} justifyContent="flex-end">
                              <ActionButton
                                size="small"
                                tone="teal"
                                onClick={() =>
                                  navigate(`/tasks/edit/${row.id}`, {
                                    state: { redirectPath: `/sops/edit/${id}` },
                                  })
                                }
                              >
                                View / Edit
                              </ActionButton>
                              <ActionButton
                                size="small"
                                tone="ghost"
                                onClick={() =>
                                  navigate("/createTask", {
                                    state: {
                                      mode: "version",
                                      taskId: row.id,
                                      changeType: "MINOR",
                                      projectId: id,
                                      redirectPath: `/sops/edit/${id}`,
                                      initialValues: row,
                                    },
                                  })
                                }
                              >
                                Minor Change
                              </ActionButton>

                              <ActionButton
                                size="small"
                                tone="ghost"
                                onClick={() =>
                                  navigate("/createTask", {
                                    state: {
                                      mode: "version",
                                      taskId: row.id,
                                      changeType: "MAJOR",
                                      projectId: id,
                                      redirectPath: `/sops/edit/${id}`,
                                      initialValues: row,
                                    },
                                  })
                                }
                              >
                                Major Change
                              </ActionButton>
                              <ActionButton
                                size="small"
                                tone="danger"
                                color="error"
                                onClick={() => {
                                  setSelectedTask(row);
                                  setDeleteModalOpen(true);
                                }}
                              >
                                Delete
                              </ActionButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                </TableBody>
              </Table>
            )}
          </Droppable>
        </DragDropContext>
      </Box>

      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <Box
          p={4}
          bgcolor="background.paper"
          borderRadius="12px"
          mx="auto"
          my="20vh"
          width="400px"
          boxShadow={24}
        >
          <Typography variant="h6">Delete Task</Typography>
          <Typography mb={2}>
            Are you sure you want to delete this task?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default EditSOPForm;